import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  X,
  Send,
  MessageSquare,
  AlertCircle,
  Utensils,
  CreditCard,
  Sparkles,
  ThumbsUp,
  ThumbsDown,
  Clock,
} from 'lucide-react';
import { toast } from '../ui/notify';
import { useBodyScrollLock } from './useBodyScrollLock';
import { useQrLanguage } from './LanguageContext';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (payload: FeedbackPayload) => void;
  /** Show 1-5 star restaurant rating section */
  showStarRating?: boolean;
  /** Label for the star rating section */
  starRatingLabel?: string;
}

export type FeedbackCategory =
  | 'complaint'
  | 'suggestion'
  | 'food'
  | 'service'
  | 'payment'
  | 'other';

export interface FeedbackPayload {
  rating: 'good' | 'bad' | null;
  starRating: number | null;
  category: FeedbackCategory | null;
  questions: string[];
  message: string;
}

// Categories and quick-questions are built inside the component so their
// labels can be translated. The static config (id / icon / color / category)
// stays out here.
const CATEGORY_CONFIG: Array<{
  id: FeedbackCategory;
  icon: any;
  color: 'red' | 'blue' | 'orange' | 'purple' | 'green' | 'gray';
}> = [
    { id: 'complaint', icon: AlertCircle, color: 'red' },
    { id: 'suggestion', icon: ThumbsUp, color: 'blue' },
    { id: 'food', icon: Utensils, color: 'orange' },
    { id: 'service', icon: Sparkles, color: 'purple' },
    { id: 'payment', icon: CreditCard, color: 'green' },
    { id: 'other', icon: MessageSquare, color: 'gray' },
  ];

const QUICK_QUESTION_CONFIG: Array<{
  id: string;
  category: FeedbackCategory;
}> = [
    { id: 'slow-service', category: 'service' },
    { id: 'cold-food', category: 'food' },
    { id: 'wrong-order', category: 'complaint' },
    { id: 'dirty-table', category: 'service' },
    { id: 'noise', category: 'other' },
    { id: 'portion-small', category: 'food' },
  ];

const colorClasses: Record<
  string,
  { border: string; bg: string; text: string; shadow: string }
> = {
  red: {
    border: 'border-red-500',
    bg: 'bg-red-50 dark:bg-red-500/10',
    text: 'text-red-600 dark:text-red-400',
    shadow: 'shadow-red-500/20',
  },
  blue: {
    border: 'border-blue-500',
    bg: 'bg-blue-50 dark:bg-blue-500/10',
    text: 'text-blue-600 dark:text-blue-400',
    shadow: 'shadow-blue-500/20',
  },
  orange: {
    border: 'border-orange-500',
    bg: 'bg-orange-50 dark:bg-orange-500/10',
    text: 'text-orange-600 dark:text-orange-400',
    shadow: 'shadow-orange-500/20',
  },
  purple: {
    border: 'border-purple-500',
    bg: 'bg-purple-50 dark:bg-purple-500/10',
    text: 'text-purple-600 dark:text-purple-400',
    shadow: 'shadow-purple-500/20',
  },
  green: {
    border: 'border-green-500',
    bg: 'bg-green-50 dark:bg-green-500/10',
    text: 'text-green-600 dark:text-green-400',
    shadow: 'shadow-green-500/20',
  },
  gray: {
    border: 'border-gray-500',
    bg: 'bg-gray-50 dark:bg-white/5',
    text: 'text-gray-600 dark:text-gray-300',
    shadow: 'shadow-gray-500/20',
  },
};

export default function FeedbackModal({ isOpen, onClose, onSubmit, showStarRating = true, starRatingLabel }: FeedbackModalProps) {
  const { t } = useQrLanguage();
  const [selectedCategory, setSelectedCategory] = useState<FeedbackCategory | null>(null);
  const [selectedQuestions, setSelectedQuestions] = useState<string[]>([]);
  const [customMessage, setCustomMessage] = useState('');
  const [rating, setRating] = useState<'good' | 'bad' | null>(null);
  const [starRating, setStarRating] = useState<number | null>(null);

  // Localized lookup tables for category and quick-question labels.
  const categoryLabels: Record<FeedbackCategory, string> = {
    complaint: t.catComplaint,
    suggestion: t.catSuggestion,
    food: t.catFood,
    service: t.catService,
    payment: t.catPayment,
    other: t.catOther,
  };

  const quickQuestionLabels: Record<string, string> = {
    'slow-service': t.qSlowService,
    'cold-food': t.qColdFood,
    'wrong-order': t.qWrongOrder,
    'dirty-table': t.qDirtyTable,
    noise: t.qNoise,
    'portion-small': t.qSmallPortion,
  };

  useBodyScrollLock(isOpen);

  useEffect(() => {
    if (!isOpen) {
      // Reset on close
      const t = setTimeout(() => {
        setSelectedCategory(null);
        setSelectedQuestions([]);
        setCustomMessage('');
        setRating(null);
        setStarRating(null);
      }, 300);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  const toggleQuestion = (questionId: string) => {
    setSelectedQuestions((prev) =>
      prev.includes(questionId) ? prev.filter((id) => id !== questionId) : [...prev, questionId],
    );
  };

  const handleSubmit = () => {
    if (!rating && !selectedCategory && selectedQuestions.length === 0 && !customMessage.trim()) {
      toast.error(t.feedbackEmptyError, { position: 'top-center' });
      return;
    }

    const payload: FeedbackPayload = {
      rating,
      starRating,
      category: selectedCategory,
      questions: selectedQuestions,
      message: customMessage.trim(),
    };

    if (onSubmit) {
      onSubmit(payload);
    }

    toast.success(t.feedbackThanks, {
      position: 'top-center',
      autoClose: 3000,
    });

    onClose();
  };

  const filteredQuestions = selectedCategory
    ? QUICK_QUESTION_CONFIG.filter((q) => q.category === selectedCategory)
    : QUICK_QUESTION_CONFIG;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[85] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm qrm-no-select"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: '100%', opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: '100%', opacity: 0 }}
            transition={{ type: 'spring', bounce: 0.18, duration: 0.55 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-lg bg-white dark:bg-[#1a1a1a] rounded-t-[32px] sm:rounded-[32px] max-h-[92vh] flex flex-col shadow-2xl border-t-2 sm:border-2 border-gray-200 dark:border-white/10"
          >
            {/* Drag indicator (mobile) */}
            <div className="absolute top-2.5 left-1/2 -translate-x-1/2 w-12 h-1.5 bg-gray-300 dark:bg-white/20 rounded-full sm:hidden" />

            {/* Header */}
            <div className="relative px-6 pt-7 pb-5 border-b-2 border-gray-100 dark:border-white/10">
              <button
                type="button"
                onClick={onClose}
                className="absolute top-5 right-5 w-10 h-10 rounded-full bg-gray-100 dark:bg-white/10 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-white/20 transition-colors active:scale-90"
                aria-label="close"
              >
                <X className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              </button>

              <div className="flex items-start gap-3 pr-12">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shadow-lg shadow-orange-500/30 shrink-0">
                  <MessageSquare className="w-7 h-7 text-white" />
                </div>
                <div className="min-w-0 mt-1">
                  <h2 className="text-xl font-black text-gray-900 dark:text-white tracking-tight leading-tight">
                    {t.feedbackTitle}
                  </h2>
                  <p className="text-sm font-semibold text-gray-500 dark:text-gray-400 mt-0.5">
                    {t.feedbackSubtitle}
                  </p>
                </div>
              </div>
            </div>

            {/* Content - Scrollable */}
            <div className="flex-1 qrm-modal-scroll scrollbar-hide p-6 space-y-6">
              {/* Star Rating (Restaurant) */}
              {showStarRating && (
                <div>
                  <h3 className="text-xs font-black text-gray-900 dark:text-white mb-3 uppercase tracking-wider">
                    {starRatingLabel || (t as any).rateRestaurant || 'Restorana qiymət verin'}
                  </h3>
                  <div className="flex items-center justify-center gap-2">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setStarRating(starRating === n ? null : n)}
                        className="transition-all active:scale-90 hover:scale-110 p-1"
                      >
                        <svg
                          className={`w-10 h-10 transition-colors ${starRating && n <= starRating
                            ? 'text-yellow-400 fill-yellow-400 drop-shadow-[0_2px_4px_rgba(250,204,21,0.4)]'
                            : 'text-gray-300 dark:text-gray-600 fill-none'
                            }`}
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={1.5}
                          fill={starRating && n <= starRating ? 'currentColor' : 'none'}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"
                          />
                        </svg>
                      </button>
                    ))}
                  </div>
                  {starRating && (
                    <p className="text-center text-xs font-bold text-yellow-600 dark:text-yellow-400 mt-2">
                      {starRating}/5 ⭐
                    </p>
                  )}
                </div>
              )}

              {/* Category Selection */}
              <div>
                <h3 className="text-xs font-black text-gray-900 dark:text-white mb-3 uppercase tracking-wider">
                  {t.selectCategory}
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {CATEGORY_CONFIG.map((category) => {
                    const Icon = category.icon;
                    const colors = colorClasses[category.color];
                    const isSelected = selectedCategory === category.id;

                    return (
                      <button
                        key={category.id}
                        type="button"
                        onClick={() =>
                          setSelectedCategory(isSelected ? null : category.id)
                        }
                        className={`relative p-4 rounded-2xl border-2 transition-all active:scale-95 ${isSelected
                          ? `${colors.border} ${colors.bg} shadow-lg ${colors.shadow}`
                          : 'border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 hover:border-gray-300'
                          }`}
                      >
                        {isSelected && (
                          <div className="absolute -top-2 -right-2 w-6 h-6 bg-gradient-to-br from-orange-500 to-amber-500 rounded-full flex items-center justify-center shadow-lg">
                            <svg
                              className="w-3.5 h-3.5 text-white"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={3}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                          </div>
                        )}
                        <Icon
                          className={`w-6 h-6 mx-auto mb-2 ${isSelected ? colors.text : 'text-gray-400 dark:text-gray-500'
                            }`}
                        />
                        <p
                          className={`text-xs font-bold text-center ${isSelected ? colors.text : 'text-gray-700 dark:text-gray-300'
                            }`}
                        >
                          {categoryLabels[category.id]}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Quick Options */}
              <div>
                <h3 className="text-xs font-black text-gray-900 dark:text-white mb-3 uppercase tracking-wider flex items-center gap-2">
                  <Clock className="w-4 h-4 text-orange-500" />
                  {t.quickOptions}
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  {filteredQuestions.map((question) => {
                    const isSelected = selectedQuestions.includes(question.id);
                    return (
                      <button
                        key={question.id}
                        type="button"
                        onClick={() => toggleQuestion(question.id)}
                        className={`p-3 rounded-xl border-2 text-left transition-all active:scale-95 ${isSelected
                          ? 'border-orange-500 bg-orange-50 dark:bg-orange-500/10 shadow-md shadow-orange-500/20'
                          : 'border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 hover:border-orange-300'
                          }`}
                      >
                        <p
                          className={`text-xs font-bold ${isSelected
                            ? 'text-orange-600 dark:text-orange-400'
                            : 'text-gray-700 dark:text-gray-300'
                            }`}
                        >
                          {quickQuestionLabels[question.id] || question.id}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Custom Message */}
              <div>
                <h3 className="text-xs font-black text-gray-900 dark:text-white mb-3 uppercase tracking-wider">
                  {t.detailedDesc}
                </h3>
                <textarea
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  placeholder={t.detailedPlaceholder}
                  className="w-full h-28 p-4 rounded-2xl border-2 border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 resize-none focus:outline-none focus:ring-4 focus:ring-orange-500/20 focus:border-orange-500 transition-all font-medium text-sm"
                  maxLength={500}
                />
                <div className="flex justify-between items-center mt-2 px-1">
                  <p className="text-[11px] font-semibold text-gray-400">{t.maxChars}</p>
                  <p className="text-[11px] font-bold text-gray-500 dark:text-gray-400">
                    {customMessage.length}/500
                  </p>
                </div>
              </div>
            </div>

            {/* Footer - Submit */}
            <div className="p-5 border-t-2 border-gray-100 dark:border-white/10 bg-gray-50/50 dark:bg-white/5 qrm-safe-bottom">
              <button
                type="button"
                onClick={handleSubmit}
                className="w-full h-14 rounded-2xl bg-gradient-to-r from-orange-500 to-amber-500 text-white font-black text-base uppercase tracking-wider shadow-xl shadow-orange-500/30 hover:shadow-2xl hover:shadow-orange-500/40 active:scale-95 transition-all flex items-center justify-center gap-3"
              >
                <Send className="w-5 h-5" />
                {t.send}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
